import React from 'react';
import { Layout, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { NS } from '../i18n';
import { UsersTableSkeleton } from '../components/UsersTableSkeleton/UsersTableSkeleton';
import { TeamsTableSkeleton } from '../components/TeamsTableSkeleton/TeamsTableSkeleton';
import { RolesGridSkeleton } from '../components/RolesGridSkeleton/RolesGridSkeleton';
// The classes this skeleton draws with live in these sheets. Imported here too
// because the skeleton is the route's Suspense fallback: it renders before the
// page's chunk — and its CSS — has loaded.
import '../../common/components/PageShell/PageShell.css';
import '../pages/OrganizationPage.css';
import '../pages/OrganizationRolesPage.css';

export type OrganizationTab = 'users' | 'teams' | 'roles';

/** The real header buttons: 40px high, radius 12. */
const CONTROL_STYLE: React.CSSProperties = { height: 40, borderRadius: 12 };

/** Widths of each tab's header buttons (Users: Import + Create new user). */
const ACTION_WIDTHS: Record<OrganizationTab, number[]> = {
  users: [110, 170],
  teams: [130],
  roles: [124],
};

/**
 * Loading state of an Organization tab: the PageShell it renders into
 * (breadcrumb, title, actions, tabs, search) around the tab's own list
 * skeleton. Uses PageShell's and the tab's own classes, so widths and the
 * mobile stacking (≤768px) match the real page.
 */
export function OrganizationPageSkeleton({ tab }: { tab: OrganizationTab }): React.ReactElement {
  const { t } = useTranslation(NS);

  return (
    <Layout.Content className="page-shell" aria-busy="true" aria-label={t('page.loadingAria')}>
      <div className="app-breadcrumb">
        <Skeleton.Input active size="small" style={{ width: 220, minWidth: 220, height: 22 }} />
      </div>
      <div className="page-shell__card">
        <header className="page-shell__header">
          <Skeleton.Input
            active
            style={{ width: 200, minWidth: 200, height: 38, borderRadius: 8 }}
          />
          <div className="page-shell__actions">
            {ACTION_WIDTHS[tab].map((width, index) => (
              <Skeleton.Button
                key={index}
                active
                style={{ ...CONTROL_STYLE, width, minWidth: width }}
              />
            ))}
          </div>
        </header>

        <div className="page-shell__toolbar">
          <div className="org-page-skeleton__tabs">
            <Skeleton.Input active block style={CONTROL_STYLE} />
          </div>
          <div className="org-card__filters">
            <div className="org-card__search">
              <Skeleton.Input active block style={CONTROL_STYLE} />
            </div>
            {tab === 'users' && (
              <div className="org-card__role-filter">
                <Skeleton.Input active block style={CONTROL_STYLE} />
              </div>
            )}
          </div>
        </div>

        <div className="page-shell__content">
          {tab === 'users' && <UsersTableSkeleton />}
          {tab === 'teams' && <TeamsTableSkeleton />}
          {tab === 'roles' && (
            // The tab opens in card view, so that is the shape to draw.
            <div className="org-roles-content">
              <div className="org-roles-view-row">
                <Skeleton.Button
                  active
                  style={{ width: 76, minWidth: 76, height: 32, borderRadius: 12 }}
                />
              </div>
              <RolesGridSkeleton />
            </div>
          )}
        </div>
      </div>
    </Layout.Content>
  );
}

/** The tab an `/organization/...` path shows; the index route redirects to users. */
export function organizationTabFromPath(pathname: string): OrganizationTab {
  if (/\/organization\/teams(\/|$)/.test(pathname)) return 'teams';
  if (/\/organization\/roles(\/|$)/.test(pathname)) return 'roles';
  return 'users';
}
